import { useEffect, useState, type CSSProperties } from 'react'
import { BRAND_LOGO_URL } from '../utils/brand-logo'

const FADE_OUT_MS = 200

/**
 * Per-appearance motion so the splash never looks identical twice. Kept tasteful:
 * only the spin's speed, direction, rotation count, easing and entrance scale
 * vary. When the user prefers reduced motion, `reduced` is set and all variation
 * collapses to a static logo (CSS also force-disables the keyframes).
 */
export type SplashVariant = {
  reduced: boolean
  style: CSSProperties
}

const SPIN_EASES = [
  'cubic-bezier(0.34, 1.2, 0.64, 1)',
  'cubic-bezier(0.22, 1, 0.36, 1)',
  'cubic-bezier(0.16, 1, 0.3, 1)',
]

function prefersReducedMotion(): boolean {
  if (typeof document !== 'undefined') {
    if (document.documentElement.dataset.a11yReducedMotion === 'true') return true
  }
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

const rand = (min: number, max: number) => min + Math.random() * (max - min)

export function makeSplashVariant(): SplashVariant {
  if (prefersReducedMotion()) {
    return { reduced: true, style: {} }
  }
  const rotations = rand(1.25, 2.25)
  const direction = Math.random() < 0.5 ? -1 : 1
  const fromDeg = Math.round(direction * rotations * 360)
  const durationMs = Math.round(rand(550, 800))
  const scaleFrom = Number(rand(0.7, 0.82).toFixed(3))
  const ease = SPIN_EASES[Math.floor(Math.random() * SPIN_EASES.length)]
  return {
    reduced: false,
    style: {
      '--splash-spin-from': `${fromDeg}deg`,
      '--splash-spin-duration': `${durationMs}ms`,
      '--splash-spin-ease': ease,
      '--splash-scale-from': scaleFrom,
    } as CSSProperties,
  }
}

type Props = {
  variant: SplashVariant
  exiting: boolean
}

export function LoadingSplashOverlay({ variant, exiting }: Props) {
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const visible = entered && !exiting

  // `pointer-events-none`: the splash is purely a visual indicator and must NOT
  // intercept clicks. It sits at `z-50` over the nav rail / bottom tab bar
  // (`z-20`); without this it swallowed the first tap on a nav button (fixed in
  // PR #76). Keep it so taps always pass through to the navigation underneath.
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading"
      style={{ transitionDuration: `${FADE_OUT_MS}ms` }}
      className={`loading-splash-overlay pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm transition-opacity ease-out ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="flex flex-col items-center px-6 text-center">
        <div className="loading-splash-logo-pulse">
          <img
            src={BRAND_LOGO_URL}
            alt=""
            style={variant.style}
            className="loading-splash-logo h-16 w-16 object-contain drop-shadow-md sm:h-20 sm:w-20"
            width={80}
            height={80}
          />
        </div>
      </div>
    </div>
  )
}
